/**
 * Application media service — single source of truth for player/org/competition media.
 * Components must not call R2 or the Worker directly.
 */

import {
  MEDIA_KINDS,
  mediaObjectPath,
  sanitizeEntityId,
} from "./mediaTypes";
import {
  deleteMediaViaWorker,
  getPublicUrl as buildPublicUrl,
  isMediaApiConfigured,
  uploadMediaViaWorker,
} from "./uploadService";

export {
  MEDIA_KINDS,
  MEDIA_KIND_CONFIG,
  ACCEPTED_IMAGE_MIME,
  MAX_UPLOAD_BYTES,
  mediaObjectPath,
  assertAcceptedImageFile,
} from "./mediaTypes";

export { compressImageForMedia, blobToDataUrl } from "./imageCompression";
export { isMediaApiConfigured } from "./uploadService";
export {
  withMediaCacheBust,
  stripMediaCacheBust,
  friendlyMediaError,
} from "./mediaUrl";

/**
 * @param {string} playerId
 * @param {File} file
 * @param {{ onProgress?: Function }} [opts]
 */
export async function uploadPlayerPhoto(playerId, file, opts = {}) {
  const id = sanitizeEntityId(playerId);
  return uploadMediaViaWorker({
    kind: MEDIA_KINDS.PLAYER,
    entityId: id,
    file,
    onProgress: opts.onProgress,
  });
}

/**
 * @param {string} orgId
 * @param {File} file
 * @param {{ onProgress?: Function }} [opts]
 */
export async function uploadOrganizationLogo(orgId, file, opts = {}) {
  const id = sanitizeEntityId(orgId);
  return uploadMediaViaWorker({
    kind: MEDIA_KINDS.ORGANIZATION,
    entityId: id,
    file,
    onProgress: opts.onProgress,
  });
}

/**
 * @param {string} competitionId
 * @param {File} file
 * @param {{ onProgress?: Function }} [opts]
 */
export async function uploadCompetitionBanner(competitionId, file, opts = {}) {
  const id = sanitizeEntityId(competitionId);
  return uploadMediaViaWorker({
    kind: MEDIA_KINDS.COMPETITION,
    entityId: id,
    file,
    onProgress: opts.onProgress,
  });
}

/**
 * @param {string} path
 */
export async function deleteMedia(path) {
  return deleteMediaViaWorker(path);
}

/**
 * @param {string} kind
 * @param {string} entityId
 */
export async function deleteEntityMedia(kind, entityId) {
  return deleteMediaViaWorker(mediaObjectPath(kind, entityId));
}

/**
 * @param {string} path
 */
export function getPublicUrl(path) {
  return buildPublicUrl(path);
}
